/**
 * Utility functions for handling number inputs without leading zeros
 */

/**
 * Handles number input change to prevent leading zeros
 * @param {string} value - The input value
 * @param {Object} options - Configuration options
 * @param {number} options.min - Minimum allowed value (default: 0)
 * @param {number} options.max - Maximum allowed value (default: Infinity)
 * @param {boolean} options.allowDecimals - Allow decimal values (default: false)
 * @param {boolean} options.allowEmpty - Allow empty string (default: true)
 * @returns {string|number} - Parsed value without leading zeros
 */
export const parseNumberInput = (value, options = {}) => {
  const {
    min = 0,
    max = Infinity,
    allowDecimals = false,
    allowEmpty = true
  } = options;

  // Allow empty string
  if (value === '' && allowEmpty) {
    return '';
  }

  // Parse the value
  const parsed = allowDecimals ? parseFloat(value) : parseInt(value);

  // Return empty string if parsing failed and empty is allowed
  if (isNaN(parsed)) {
    return allowEmpty ? '' : 0;
  }

  // Clamp to min/max
  const clamped = Math.max(min, Math.min(max, parsed));

  return clamped;
};

/**
 * Gets the display value for a number input (handles empty strings)
 * @param {string|number} value - The stored value
 * @param {string} defaultValue - Default value to show when empty (default: '')
 * @returns {string|number} - Value to display in input
 */
export const getNumberInputValue = (value, defaultValue = '') => {
  if (value === '' || value === null || value === undefined) {
    return defaultValue;
  }
  return value;
};

/**
 * Standard onChange handler for number inputs
 * @param {Function} setValue - State setter function
 * @param {Object} options - Options passed to parseNumberInput
 * @returns {Function} - onChange handler
 */
export const createNumberInputHandler = (setValue, options = {}) => {
  return (e) => {
    const value = e.target.value;
    setValue(parseNumberInput(value, options));
  };
};
